import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ApiError, apiDelete, apiFetch, apiPost } from "@/lib/api";

export interface ShareSummary {
  id: string;
  mode: "file" | "folder_snapshot" | "folder_live_mirror";
  projectId: string;
  projectName: string | null;
  fileId: string | null;
  fileName: string | null;
  folderId: string | null;
  folderPath: string | null;
  versionPinned: number | null;
  expiresAt: string;
  emailGateEnabled: boolean;
  hasPin: boolean;
  message: string | null;
  recipientEmails: string[] | null;
  createdByUserId: string;
  revokedAt: string | null;
  createdAt: string;
  accessCount: number;
  rawToken: string | null;
}

interface CreateShareInput {
  fileId: string;
  expiresInDays?: number;
  emailGateEnabled?: boolean;
  pin?: string;
  message?: string;
  recipientEmails?: string[];
  confirmRestricted?: boolean;
  restrictedReason?: string;
}

interface CreateShareResponse {
  ok: true;
  share: ShareSummary;
  rawToken: string;
  shareUrl: string;
}

export const documentShareKeys = {
  // "all" is the sentinel for cross-project queries.
  list: (projectId: string | "all", status?: string) =>
    ["document-shares", projectId, status ?? "all"] as const,
  file: (fileId: string) => ["document-shares", "file", fileId] as const,
};

/**
 * Builds an absolute public share URL from a raw token. Returns null when
 * the token is missing (legacy shares stored before raw_token was added).
 */
export function buildShareUrlFromToken(
  rawToken: string | null,
  mode: ShareSummary["mode"],
): string | null {
  if (!rawToken) return null;
  const prefix = mode === "file" ? "file" : "folder";
  return `${window.location.origin}/share/${prefix}/${rawToken}`;
}

export function useProjectShares(
  projectId: string | "all",
  status?: "active" | "revoked" | "expired",
) {
  return useQuery({
    queryKey: documentShareKeys.list(projectId, status),
    queryFn: () => {
      // "all" hits the cross-project endpoint; everything else hits the
      // project-scoped endpoint for parity with the existing routes.
      if (projectId === "all") {
        const qs = new URLSearchParams();
        if (status) qs.set("status", status);
        const suffix = qs.toString() ? `?${qs.toString()}` : "";
        return apiFetch<{ data: ShareSummary[] }>(`/document-shares${suffix}`).then((r) => r.data);
      }
      const qs = status ? `?status=${status}` : "";
      return apiFetch<{ data: ShareSummary[] }>(`/projects/${projectId}/document-shares${qs}`).then(
        (r) => r.data,
      );
    },
    enabled: !!projectId,
  });
}

/**
 * Active shares (file-mode) for a specific file. Used by the file-context
 * share dialog so the user sees existing links before creating a new one.
 */
export function useFileShares(fileId: string | null) {
  return useQuery({
    queryKey: documentShareKeys.file(fileId ?? ""),
    queryFn: () =>
      apiFetch<{ data: ShareSummary[] }>(`/files/${fileId}/shares`).then((r) => r.data),
    enabled: !!fileId,
  });
}

export function useCreateShare(projectId: string) {
  const qc = useQueryClient();
  return useMutation<CreateShareResponse, Error, CreateShareInput>({
    mutationFn: (input) =>
      apiPost<CreateShareResponse>(`/projects/${projectId}/document-shares`, input),
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ["document-shares"] });
      if (resp.share.fileId) {
        qc.invalidateQueries({ queryKey: documentShareKeys.file(resp.share.fileId) });
      }
      toast.success("Share link created");
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        // Caller handles restricted_class_unconfirmed / hard_blocked dialogs.
        return;
      }
      toast.error(err.message);
    },
  });
}

interface CreateFolderSnapshotInput {
  folderId: string;
  expiresInDays?: number;
  emailGateEnabled?: boolean;
  pin?: string;
  message?: string;
  recipientEmails?: string[];
  confirmRestricted?: boolean;
  restrictedReason?: string;
}

export function useCreateFolderSnapshotShare(projectId: string) {
  const qc = useQueryClient();
  return useMutation<CreateShareResponse & { itemCount: number }, Error, CreateFolderSnapshotInput>(
    {
      mutationFn: (input) =>
        apiPost(`/projects/${projectId}/document-shares/folder-snapshot`, input),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ["document-shares"] });
        toast.success("Folder snapshot shared");
      },
      onError: (err) => {
        if (err instanceof ApiError && err.status === 409) return;
        toast.error(err.message);
      },
    },
  );
}

export function useRevokeShare(projectId: string | "all") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shareId, reason }: { shareId: string; reason?: string }) =>
      apiDelete(`/document-shares/${shareId}`).then(async () => {
        // apiDelete returns void; mimic the request body via fetch directly for reason.
        if (reason) {
          await fetch(`/api/document-shares/${shareId}`, {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ reason }),
          });
        }
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["document-shares"] });
    },
    onError: (err) => toast.error(err.message),
  });
}
